import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  TrendingUp,
  Download,
  CheckCircle,
  Clock,
  Wallet,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Commission {
  id: string
  period: string
  referrals: number
  totalDeposit: number
  commissionRate: number
  amount: number
  status: 'pending' | 'approved' | 'paid'
  paidAt?: string
}

const mockCommissions: Commission[] = [
  { id: '1', period: 'พ.ย. 2025 (1-15)', referrals: 8, totalDeposit: 285000, commissionRate: 35, amount: 9975, status: 'pending' },
  { id: '2', period: 'พ.ย. 2025 (16-30)', referrals: 4, totalDeposit: 125000, commissionRate: 35, amount: 4375, status: 'pending' },
  { id: '3', period: 'ต.ค. 2025 (1-15)', referrals: 6, totalDeposit: 195000, commissionRate: 35, amount: 6825, status: 'paid', paidAt: '2025-10-20' },
  { id: '4', period: 'ต.ค. 2025 (16-31)', referrals: 4, totalDeposit: 158000, commissionRate: 35, amount: 5530, status: 'paid', paidAt: '2025-11-05' },
  { id: '5', period: 'ก.ย. 2025 (1-15)', referrals: 7, totalDeposit: 210000, commissionRate: 35, amount: 7350, status: 'paid', paidAt: '2025-09-20' },
  { id: '6', period: 'ก.ย. 2025 (16-30)', referrals: 8, totalDeposit: 245000, commissionRate: 35, amount: 8575, status: 'paid', paidAt: '2025-10-05' },
]

export function AffiliateCommissions() {
  const [commissions] = useState(mockCommissions)

  const totalPending = commissions.filter(c => c.status === 'pending').reduce((acc, c) => acc + c.amount, 0)
  const totalPaid = commissions.filter(c => c.status === 'paid').reduce((acc, c) => acc + c.amount, 0)
  const currentRate = 35

  const getStatusBadge = (status: Commission['status']) => {
    const config = {
      pending: { label: 'รอจ่าย', icon: <Clock className="h-3 w-3" />, className: 'bg-amber-500/10 text-amber-500' },
      approved: { label: 'อนุมัติ', icon: <CheckCircle className="h-3 w-3" />, className: 'bg-blue-500/10 text-blue-500' },
      paid: { label: 'จ่ายแล้ว', icon: <CheckCircle className="h-3 w-3" />, className: 'bg-win/10 text-win' },
    }
    const { label, icon, className } = config[status]
    return <Badge variant="secondary" className={cn("border-0 gap-1", className)}>{icon} {label}</Badge>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">คอมมิชชั่น</h1>
          <p className="text-muted-foreground">ประวัติและยอดคอมมิชชั่นของคุณ</p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          ส่งออก
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">รอจ่าย</p>
                <p className="text-2xl font-bold text-amber-500">฿{totalPending.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <CheckCircle className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">จ่ายแล้วทั้งหมด</p>
                <p className="text-2xl font-bold text-win">฿{totalPaid.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">อัตราคอมมิชชั่น</p>
                <p className="text-2xl font-bold text-purple-500">{currentRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Wallet className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ถอนได้</p>
                <p className="text-2xl font-bold text-blue-500">฿{totalPending.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Rate Info */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-purple-500/20">
              <TrendingUp className="h-6 w-6 text-purple-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">อัตราคอมมิชชั่นของคุณ: {currentRate}%</h3>
              <p className="text-sm text-muted-foreground">
                คุณได้รับ {currentRate}% จากยอดฝากครั้งแรกของสมาชิกที่คุณแนะนำ
              </p>
            </div>
            <Button variant="outline" size="sm">
              ดูโครงสร้างคอมมิชชั่น
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ประวัติคอมมิชชั่น</CardTitle>
          <CardDescription>รายการคอมมิชชั่นตามงวด</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>งวด</TableHead>
                <TableHead className="text-center">สมาชิกใหม่</TableHead>
                <TableHead className="text-right">ยอดฝากรวม</TableHead>
                <TableHead className="text-center">อัตรา</TableHead>
                <TableHead className="text-right">คอมมิชชั่น</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>วันที่จ่าย</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissions.map((comm) => (
                <TableRow key={comm.id}>
                  <TableCell className="font-medium">{comm.period}</TableCell>
                  <TableCell className="text-center">{comm.referrals} คน</TableCell>
                  <TableCell className="text-right">฿{comm.totalDeposit.toLocaleString()}</TableCell>
                  <TableCell className="text-center">{comm.commissionRate}%</TableCell>
                  <TableCell className="text-right font-semibold text-win">฿{comm.amount.toLocaleString()}</TableCell>
                  <TableCell>{getStatusBadge(comm.status)}</TableCell>
                  <TableCell className="text-muted-foreground">{comm.paidAt || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          แสดง {commissions.length} รายการ
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm">
            1
          </Button>
          <Button variant="outline" size="sm" disabled>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}










